import {
  Box,
  type BoxProps,
  Heading,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/layout";
import { Tooltip, useMediaQuery } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import { percent } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import { Fragment } from "react";
import { PieGraph, type PieGraphProps } from "../../graphs/pie";

const VISIBLE_ITEMS_COUNT = 6;

type Props = {
  title: string;
  isResponsive?: boolean;
  pieHeight?: BoxProps["height"];
} & PieGraphProps;

export const PieList = observer(function PieList({
  data,
  title,
  isResponsive,
  outerRadius,
  innerRadius,
  pieHeight = "32",
}: Props) {
  const [isLargerThan1513] = useMediaQuery("(min-width: 1513px)");
  const hasHiddenItems = data.length > VISIBLE_ITEMS_COUNT;
  const visibleItems = data.slice(0, VISIBLE_ITEMS_COUNT);
  const remainingItems = data.slice(VISIBLE_ITEMS_COUNT);
  const remainingItemsValueSum = remainingItems.reduce(
    (sum, share) => sum + share.value,
    0,
  );

  const hideItemList = isResponsive && !isLargerThan1513;

  return (
    <VStack align="start" w={isResponsive ? undefined : "full"}>
      <Heading fontSize="lg">{title}</Heading>

      <Tooltip
        isDisabled={!hasHiddenItems || hideItemList}
        label={
          <Text>
            {remainingItems.map((share) => (
              <Fragment key={share.name}>
                {formatShare(share)}
                <br />
              </Fragment>
            ))}
          </Text>
        }
      >
        <HStack alignItems="start" w="full">
          <Box h={pieHeight} w="full">
            <PieGraph
              data={data}
              outerRadius={outerRadius}
              innerRadius={innerRadius}
            />
          </Box>

          <VStack
            align="start"
            w="full"
            spacing="0"
            display={hideItemList ? "none" : undefined}
          >
            {visibleItems.map((share) => (
              <Text key={share.name} fontWeight="medium" fontSize="xs">
                {formatShare(share)}
              </Text>
            ))}

            {hasHiddenItems && (
              <Text fontWeight="medium" fontSize="xs">
                {formatShare({ name: t`Other`, value: remainingItemsValueSum })}
              </Text>
            )}
          </VStack>
        </HStack>
      </Tooltip>
    </VStack>
  );
});

function formatShare(share: PieGraphProps["data"][number]) {
  return `${share.name} ${percent.format(share.value)}`;
}
